import { Component, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { TenantContextService } from 'shared/core';
import { AdminAuthService } from './admin-auth.service';
import { homeRouteFor, KF_APP_CONFIG } from './kf-app.config';
import { TenantAccess } from './tenant.models';
import { unitTypeColor, unitTypeLabel } from './unit-type-style';

/**
 * Lists the tenants the signed-in user may open and records the choice.
 *
 * Every tenant is selectable, whatever its vertical: one application serves
 * them all, and which areas a tenant can reach is decided at the routes and
 * the menu, not here. A tenant lands on its unit type's home route.
 */
@Component({
  selector: 'kf-tenant-select',
  imports: [NzButtonModule, NzIconModule, NzTagModule, NzTooltipModule],
  templateUrl: './tenant-select.component.html',
  styleUrl: './tenant-select.component.less',
})
export class TenantSelectComponent {
  readonly app = inject(KF_APP_CONFIG);
  private readonly msal = inject(MsalService);
  private readonly adminAuth = inject(AdminAuthService);
  private readonly context = inject(TenantContextService);
  private readonly router = inject(Router);

  readonly tenants = signal<TenantAccess[]>([]);
  readonly selected = signal<TenantAccess | null>(null);
  readonly loading = signal(true);
  readonly savingPrimary = signal(false);
  readonly opening = signal(false);
  readonly error = signal<string | null>(null);

  readonly canContinue = computed(() => !!this.selected());
  readonly hasMultiple = computed(() => this.tenants().length > 1);
  readonly hasPrimary = computed(() => this.tenants().some((t) => t.IsPrimaryTenant));

  constructor() {
    if (this.msal.instance.getAllAccounts().length === 0) {
      this.router.navigate(['/login']);
      return;
    }
    this.load();
  }

  /** Fresh from the Admin API every time; the cached list only covers a failed call. */
  load(): void {
    this.loading.set(true);
    this.error.set(null);
    this.adminAuth.login().subscribe({
      next: (response) => {
        if (!response.Success) {
          this.error.set(response.Message || 'Sign-in was not accepted. Please try again.');
          this.loading.set(false);
          return;
        }
        if (response.User) this.context.setCentralUser(response.User);
        const tenants = response.AccessibleTenants ?? [];
        this.context.setAccessibleTenants(tenants);
        this.apply(tenants);
      },
      error: () => {
        const cached = this.context.getAccessibleTenants();
        if (cached.length > 0) {
          this.apply(cached);
          return;
        }
        this.error.set('The sign-in service could not be reached. Please try again in a moment.');
        this.loading.set(false);
      },
    });
  }

  private apply(tenants: TenantAccess[]): void {
    this.tenants.set(tenants);
    this.loading.set(false);

    if (tenants.length === 0) {
      this.error.set('You do not have access to any company. Please contact your administrator.');
      return;
    }

    // One tenant in total: nothing to choose.
    if (tenants.length === 1) {
      void this.open(tenants[0]);
      return;
    }

    // A primary company opens by itself when the picker is reached with nothing
    // chosen — straight after sign-in. That is rexine's behaviour, and a user
    // with a primary should not pay a click for the library's arrival. Reached
    // from "Switch Tenant" a company IS chosen, and the user is here precisely
    // to pick a different one, so it is preselected and waits. Two companies
    // marked primary is a data fault the picker cannot resolve for the user, so
    // that also waits.
    const primaries = tenants.filter((t) => t.IsPrimaryTenant);
    if (primaries.length === 1 && !this.context.hasTenantSelected()) {
      void this.open(primaries[0]);
      return;
    }

    this.selected.set(primaries[0] ?? null);
  }

  pick(tenant: TenantAccess): void {
    this.selected.set(tenant);
  }

  continue(): void {
    const tenant = this.selected();
    if (tenant) void this.open(tenant);
  }

  /**
   * Records the choice and waits for the company profile before navigating.
   *
   * The wait is rexine's, and it is not politeness: the shell's header, every
   * print header and the costing screens read CompanyInfo synchronously on
   * their first render. Navigating before the profile lands shows a company
   * with no name and prints with no address, and nothing re-renders when it
   * arrives.
   */
  private async open(tenant: TenantAccess): Promise<void> {
    this.opening.set(true);
    try {
      await this.context.selectTenantAsync(tenant);
      await this.router.navigate([homeRouteFor(this.app, tenant.UnitType)]);
    } catch {
      this.error.set('Failed to load company information');
    } finally {
      this.opening.set(false);
    }
  }

  setPrimary(tenant: TenantAccess | null, event: Event): void {
    event.stopPropagation();
    const user = this.context.getCentralUser();
    if (!user) return;
    this.savingPrimary.set(true);
    this.adminAuth.setPrimaryTenant(user.UserId, tenant?.TenantCompanyId ?? null).subscribe({
      next: () => {
        this.tenants.update((list) =>
          list.map((t) => ({ ...t, IsPrimaryTenant: !!tenant && t.TenantCompanyId === tenant.TenantCompanyId }))
        );
        this.savingPrimary.set(false);
      },
      error: () => this.savingPrimary.set(false),
    });
  }

  color = unitTypeColor;
  label = unitTypeLabel;
}
